import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Group from "@mapmycustomers/shared/types/entity/Group";
import Person from "@mapmycustomers/shared/types/entity/Person";
import File from "@mapmycustomers/shared/types/File";
import GeoAddress from "@mapmycustomers/shared/types/GeoAddress";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { FormItem, TextField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import AddressFormItem from "@app/component/createEditEntity/AddressFormItem";
import CompanyFile from "@app/component/createEditEntity/CreateCompanyModal/components/File";
import PersonFile from "@app/component/createEditEntity/CreatePersonModal/components/File";
import { getAddress } from "@app/component/createEditEntity/util/getAddress";
import FormFields from "@app/component/FormFields";
import PersonRelationshipField from "@app/component/FormFields/components/Associations/PersonRelationshipField";
import PersonCompanyField from "@app/component/FormFields/components/ParentCompanyField/PersonCompanyField";
import PersonNameField from "@app/component/FormFields/components/PersonNameField";
import personCompanyFieldMessages from "@app/component/FormFields/components/PersonNameField/messages";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import { convertPlaceToEntity } from "@app/component/leadFinder/utils/convertPlaceToEntity";
import { IWizardStep, StandardStep } from "@app/component/wizard";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import useDefaultSourceObjectKey from "@app/util/hook/useDefaultSourceObjectKey";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useSchema from "@app/util/hook/useSchema";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";
import getLayoutModelByEntityType from "@app/util/layout/impl";
import removeUndefinedFields from "@app/util/removeUndefinedFields";

import AddRecordsWizardState from "../../AddRecordsWizardState";
import useStepTitle from "../../useStepTitle";

import styles from "./FillFieldsStep.module.scss";
import messages from "./messages";

const noFilter = () => true;

type LeadEntitiesFillState = Record<keyof Company | keyof Person, "partial" | boolean | undefined>;

type LeadEntitiesFillStateField = keyof LeadEntitiesFillState;

type FormValues = Omit<Partial<Company | Person>, "files" | "groups" | "user"> & {
  addressDetails?: GeoAddress;
  customFields?: Record<CustomField["id"], CustomFieldValue>;
  files?: File[];
  groups?: Set<Group["id"]>;
  latitude?: string;
  longitude?: string;
  parentCompanyId?: Company["id"];
  personId?: Person["id"]; // for companies
  user?: User["id"];
};

const phoneFieldNames: string[] = [CompanyFieldName.PHONE, PersonFieldName.PHONE];
const addressFieldNames: string[] = [CompanyFieldName.ADDRESS, PersonFieldName.ADDRESS];

const leadEntitiesFillStateFields: LeadEntitiesFillStateField[] = [
  "email",
  "firstName",
  "lastName",
  "name",
  "phone",
  "website",
];

interface AddRecordsStepProps extends IWizardStep<AddRecordsWizardState> {
  currentUserId: User["id"];
}

const FillFieldsStep = ({
  currentUserId,
  onFinish,
  onSetState,
  state: { entityType, layout: initialLayout, selectedLeads: leads },
  ...props
}: AddRecordsStepProps) => {
  const intl = useIntl();

  const [form] = useForm<FormValues>();
  const defaultSourceObject = useDefaultSourceObjectKey(entityType);
  const messageArgs = useSourceObjectMessageArgs(entityType, defaultSourceObject);
  const initialFormValues: FormValues = useMemo(() => {
    if (leads.length !== 1) {
      return {
        sourceObject: defaultSourceObject,
        user: currentUserId,
      };
    }

    const { geoAddress, geoPoint, groups, ...entity } = removeUndefinedFields(
      convertPlaceToEntity(entityType, leads[0])
    );

    return {
      ...entity,
      addressDetails: geoAddress,
      groups: groups ? new Set(groups.map((group) => group.id)) : undefined,
      latitude: String(geoPoint?.coordinates[1]),
      longitude: String(geoPoint?.coordinates[0]),
      sourceObject: defaultSourceObject,
      user: currentUserId,
    };
  }, [leads, entityType, currentUserId, defaultSourceObject]);

  const title = useStepTitle(leads.length);
  const layoutModel = getLayoutModelByEntityType(entityType);
  const [layout, setLayout] = useState<FormLayout>(initialLayout ?? layoutModel.defaultLayout);
  const [formContainer, handleFinishFail] = useErroredFieldsScroller();

  const fieldModel = useMemo(() => getFieldModelByEntityType(entityType), [entityType]);
  const [actualSchema] = useSchema({
    entityType,
    filterFields: noFilter,
    isCreateForm: true,
    layout,
    sourceObject: defaultSourceObject,
  });

  const hasRequiredUniqueFields = useMemo(
    () =>
      actualSchema.some((schemaField) => {
        const field = fieldModel.getByPlatformName(schemaField.field);
        return (
          schemaField.required &&
          field?.isUnique &&
          // all these fields can be now marked unique, but
          // they are auto-populated from leads, so
          // we don't care checking them
          field?.name !== CommonFieldName.NAME &&
          field?.name !== PersonFieldName.FIRST_NAME &&
          field?.name !== PersonFieldName.LAST_NAME &&
          field?.name !== CommonFieldName.EMAIL &&
          field?.name !== CompanyFieldName.WEBSITE &&
          field?.name !== CompanyFieldName.PHONE &&
          !field?.hasAnyFeature(FieldFeature.ADDRESS)
        );
      }),
    [actualSchema, fieldModel]
  );

  const leadEntities: (Partial<Company> | Partial<Person>)[] = useMemo(
    () => leads.map((lead) => removeUndefinedFields(convertPlaceToEntity(entityType, lead))),
    [entityType, leads]
  );

  const leadEntitiesFillState: LeadEntitiesFillState = useMemo(() => {
    return leadEntities.reduce((state, leadEntity) => {
      for (const field of leadEntitiesFillStateFields) {
        const fieldState = state[field];
        if (isDefined(leadEntity[field as keyof typeof leadEntity])) {
          state[field] = fieldState === undefined || fieldState === true ? true : "partial";
        } else {
          state[field] = fieldState === true || fieldState === "partial" ? "partial" : false;
        }
      }
      return state;
    }, {} as LeadEntitiesFillState);
  }, [leadEntities]);

  const isAutoFillFieldLocked = useCallback(
    (field: LeadEntitiesFillStateField) => {
      const fieldState = leadEntitiesFillState[field];
      return fieldState === true;
    },
    [leadEntitiesFillState]
  );

  const getAutoFillFieldPlaceholder = useCallback(
    (field: LeadEntitiesFillStateField) => {
      const fieldState = leadEntitiesFillState[field];
      return fieldState ? intl.formatMessage(messages.autoFilledPlaceholder) : undefined;
    },
    [intl, leadEntitiesFillState]
  );

  const getAutoFillFieldCaption = useCallback(
    (field: LeadEntitiesFillStateField, fieldName: string) => {
      const fieldState = leadEntitiesFillState[field];
      return fieldState === "partial"
        ? intl.formatMessage(messages.partiallyFilledCaption, {
            fieldName,
          })
        : undefined;
    },
    [intl, leadEntitiesFillState]
  );

  const getAutoFillTextFormItemProps = useCallback(
    (field: LeadEntitiesFillStateField, required: boolean | undefined) => ({
      name: field,
      required,
      rules: required && !isAutoFillFieldLocked(field) ? [{ required }] : undefined,
    }),
    [isAutoFillFieldLocked]
  );

  const getAutoFillTextFieldProps = useCallback(
    (field: LeadEntitiesFillStateField, fieldName: string) => ({
      caption: getAutoFillFieldCaption(field, fieldName),
      locked: isAutoFillFieldLocked(field),
      placeholder: getAutoFillFieldPlaceholder(field),
    }),
    [getAutoFillFieldCaption, isAutoFillFieldLocked, getAutoFillFieldPlaceholder]
  );

  const handleFormFinish = useCallback(
    ({
      addressDetails,
      customFields,
      files, // exclude files from entity, they'll be added back in saga
      groups,
      latitude,
      longitude,
      parentCompanyId,
      personId,
      sourceObject,
      user,
      ...entity
    }: FormValues) => {
      const entityAddress = getAddress({ addressDetails, latitude, longitude });
      onSetState({
        customFields:
          customFields && Object.keys(customFields).length > 0
            ? Object.values(customFields).filter(isDefined)
            : undefined,
        entity: {
          ...entityAddress,
          ...entity,
          color: entity.color ?? Color.BLACK,
          geoAddress: addressDetails,
          user: user ? { id: user } : undefined,
          ...(entityType === EntityType.COMPANY && parentCompanyId
            ? { parentAccount: { id: parentCompanyId } as Company }
            : {}),
        },
        groupIds: groups ? Array.from(groups) : undefined,
        layout,
        personId,
      });
      onFinish();
    },
    [layout, onFinish, onSetState, entityType]
  );

  return (
    <StandardStep
      {...props}
      className={styles.stepContent}
      containerClassName={styles.stepContainer}
      nextButtonDisabled={hasRequiredUniqueFields && leads.length > 1}
      nextButtonText={intl.formatMessage(
        entityType === EntityType.COMPANY
          ? messages.finishButtonTitleCompany
          : messages.finishButtonTitlePerson,
        { ...messageArgs, count: leads.length }
      )}
      nextButtonTooltipProps={
        hasRequiredUniqueFields && leads.length > 1
          ? {
              title: intl.formatMessage(messages.uniqueFieldRequiredTooltip),
              trigger: ["hover"],
            }
          : undefined
      }
      onGoForward={form.submit}
      title={
        <Space>
          <span>{title}</span>
          <FormLayoutSelector entityType={entityType} layout={layout} onChange={setLayout} />
        </Space>
      }
    >
      <div ref={formContainer}>
        {leads.length > 1 && (
          <div className={styles.autoFilledValuesCaption}>
            {intl.formatMessage(messages.autoFilledValuesCaption)}
          </div>
        )}
        <Form
          form={form}
          initialValues={initialFormValues}
          layout="vertical"
          onFinish={handleFormFinish}
          onFinishFailed={handleFinishFail}
        >
          <FormItem hidden name="sourceObject" />
          <FormFields
            autoFocus={false}
            disableUniqueFields={leads.length > 1}
            entityType={entityType}
            fileComponent={entityType === EntityType.COMPANY ? CompanyFile : PersonFile}
            isCreateForm
            layout={layout}
          >
            <ConditionalFormField fieldName={PersonFieldName.COMPANIES}>
              {({ disabled, required }: ConditionalFormFieldRenderProps) => (
                <PersonCompanyField allowAdd={!disabled} disabled={disabled} required={required} />
              )}
            </ConditionalFormField>
            <ConditionalFormField fieldName={CompanyFieldName.PERSON}>
              {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                <PersonRelationshipField
                  allowAdd={!disabled}
                  disabled={disabled}
                  label={label}
                  required={required}
                />
              )}
            </ConditionalFormField>

            {leads.length === 1
              ? [
                  <ConditionalFormField fieldName={addressFieldNames} key="addressEditable">
                    {({ required }: ConditionalFormFieldRenderProps) => (
                      <AddressFormItem required={required} />
                    )}
                  </ConditionalFormField>,
                ]
              : [
                  <ConditionalFormField fieldName={addressFieldNames} key="addressAutoFilled">
                    {({ required }: ConditionalFormFieldRenderProps) => (
                      // it should not be possible to edit address field in such case
                      <AddressFormItem
                        disabled
                        locked
                        placeholder={intl.formatMessage(messages.autoFilledPlaceholder)}
                        required={required}
                      />
                    )}
                  </ConditionalFormField>,
                  <ConditionalFormField fieldName={PersonFieldName.FIRST_NAME} key="firstName">
                    {({ actualSchema }: ConditionalFormFieldRenderProps) => (
                      <PersonNameField
                        actualSchema={actualSchema!}
                        caption={{
                          firstName: getAutoFillFieldCaption(
                            "firstName",
                            intl.formatMessage(personCompanyFieldMessages.firstName)
                          ),
                          lastName: getAutoFillFieldCaption(
                            "lastName",
                            intl.formatMessage(personCompanyFieldMessages.lastName)
                          ),
                        }}
                        disabled={{
                          firstName: isAutoFillFieldLocked("firstName"),
                          lastName: isAutoFillFieldLocked("lastName"),
                        }}
                        placeholder={{
                          firstName: getAutoFillFieldPlaceholder("firstName"),
                          lastName: getAutoFillFieldPlaceholder("lastName"),
                        }}
                      />
                    )}
                  </ConditionalFormField>,
                  <ConditionalFormField fieldName={CompanyFieldName.NAME} key="name">
                    {({ label, required }: ConditionalFormFieldRenderProps) => (
                      <FormItem
                        label={label}
                        {...getAutoFillTextFormItemProps("name", required)}
                        showLabel={false}
                      >
                        <TextField
                          label={label}
                          {...getAutoFillTextFieldProps("name", label as string)}
                        />
                      </FormItem>
                    )}
                  </ConditionalFormField>,
                  <ConditionalFormField fieldName={phoneFieldNames} key="phone">
                    {({ label, required }: ConditionalFormFieldRenderProps) => (
                      <FormItem
                        label={label}
                        {...getAutoFillTextFormItemProps("phone", required)}
                        showLabel={false}
                      >
                        <TextField
                          label={label}
                          {...getAutoFillTextFieldProps("phone", label as string)}
                        />
                      </FormItem>
                    )}
                  </ConditionalFormField>,
                  <ConditionalFormField fieldName={CompanyFieldName.WEBSITE} key="website">
                    {({ label, required }: ConditionalFormFieldRenderProps) => (
                      <FormItem
                        label={label}
                        showLabel={false}
                        {...getAutoFillTextFormItemProps("website", required)}
                      >
                        <TextField
                          label={label}
                          {...getAutoFillTextFieldProps("website", label as string)}
                        />
                      </FormItem>
                    )}
                  </ConditionalFormField>,
                ]}
          </FormFields>
        </Form>
      </div>
    </StandardStep>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
});

export default connect(mapStateToProps)(FillFieldsStep);
